import { Group, Paper, Stack, Text } from "@mantine/core";
import { IconArrowsExchange } from "@tabler/icons-react";
import { SupportTicketEvent } from "@/app/@types/support";
import { capitalized } from "@/app/_helpers/string";
import { SUPPORT_STATUSES } from "@/app/_constants/support";
import useFormatDate from "@/app/_hooks/useFormatDate";
import SupportAttachments from "./SupportAttachments";

interface SupportEventTimelineProps {
  events: SupportTicketEvent[];
  // "user": the customer's drawer — replies only, date-only stamps on team
  // messages, team messages tinted. "team": the admin view — full audit with
  // status changes as slim log lines, customer messages tinted.
  perspective?: "user" | "team";
}

const SupportEventTimeline = ({
  events,
  perspective = "user",
}: SupportEventTimelineProps) => {
  const formatDate = useFormatDate();
  const isAdminView = perspective === "team";
  const visibleEvents = isAdminView
    ? events
    : events.filter((event) => event.type === "reply");

  return (
    <Stack gap="sm">
      {visibleEvents.map((event) => {
        const isTeam = event.author === "team";
        const when = event.createdAt?.toDate
          ? formatDate(event.createdAt, isAdminView || !isTeam)
          : "";

        if (event.type === "status_change") {
          const statusLabel = event.toStatus
            ? SUPPORT_STATUSES[event.toStatus]?.label ?? event.toStatus
            : "";
          return (
            <Group key={event.uid} gap={6} justify="center" wrap="nowrap">
              <IconArrowsExchange
                size={12}
                color="var(--mantine-color-dimmed)"
              />
              <Text size="xs" c="dimmed">
                {isTeam ? "Support" : capitalized(event.authorName)}{" "}
                set status to {statusLabel} · {when}
              </Text>
            </Group>
          );
        }

        const highlighted = isAdminView ? !isTeam : isTeam;

        return (
          <Paper
            key={event.uid}
            withBorder
            radius="md"
            p="md"
            bg={
              highlighted
                ? "var(--mantine-color-default-hover)"
                : "var(--mantine-color-body)"
            }
          >
            <Group gap="xs" justify="space-between" wrap="nowrap" mb={6}>
              <Text size="sm" fw={700} truncate="end" flex={1} miw={0}>
                {isTeam
                  ? "Support"
                  : capitalized(event.authorName) || "You"}
              </Text>
              <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                {when}
              </Text>
            </Group>
            {event.body ? (
              <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                {event.body}
              </Text>
            ) : null}
            <SupportAttachments urls={event.files} />
          </Paper>
        );
      })}
    </Stack>
  );
};

export default SupportEventTimeline;
